"use client"
import { useEffect, useEffectEvent } from "react"

export type RecordUpdateScope = "tasks" | "journeys"
const eventName = "danaro-records-updated"
type Update = { scopes: RecordUpdateScope[] }
export function publishRecordUpdate(scopes: RecordUpdateScope[]) {
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent<Update>(eventName, { detail: { scopes: [...new Set(scopes)] } }))
}

/** Coalesce record receipts; pause background-tab fetches until the document is visible. */
export function subscribeRecordUpdates(callback: (scopes: RecordUpdateScope[]) => void) {
  const pending = new Set<RecordUpdateScope>()
  let timer: ReturnType<typeof setTimeout> | undefined
  const flush = () => {
    clearTimeout(timer)
    if (!pending.size || document.visibilityState !== "visible") return
    const scopes = [...pending]; pending.clear(); callback(scopes)
  }
  const receive = (event: Event) => {
    const scopes = (event as CustomEvent<Update>).detail?.scopes
    if (!Array.isArray(scopes)) return
    for (const scope of scopes) if (scope === "tasks" || scope === "journeys") pending.add(scope)
    clearTimeout(timer); timer = setTimeout(flush, 180)
  }
  window.addEventListener(eventName, receive); document.addEventListener("visibilitychange", flush)
  return () => { clearTimeout(timer); window.removeEventListener(eventName, receive); document.removeEventListener("visibilitychange", flush) }
}

export function useRecordUpdates(scopes: readonly RecordUpdateScope[], refresh: () => void) {
  const receive = useEffectEvent((changed: RecordUpdateScope[]) => { if (changed.some(scope => scopes.includes(scope))) refresh() })
  useEffect(() => subscribeRecordUpdates(changed => receive(changed)), [])
}
